import { FormEvent, useEffect, useRef, useState } from 'react';

import type { Autenticar, CredenciaisLogin, ErroHttp } from '../model/types';

interface LoginFormProps {
    aoAutenticar: Autenticar;
    indisponivel?: boolean;
    aoTentarNovamente?: () => void;
}

type ErrosCampo = Partial<Record<keyof CredenciaisLogin, string>>;

function validar(email: string, senha: string): ErrosCampo {
    const erros: ErrosCampo = {};
    const emailNormalizado = email.trim().toLowerCase();

    if (!emailNormalizado || emailNormalizado.length > 254 || !/^\S+@\S+\.\S+$/.test(emailNormalizado)) {
        erros.email = 'Informe um e-mail valido.';
    }
    if (!senha) erros.senha = 'Informe sua senha.';
    if (senha.length > 128) erros.senha = 'A senha informada e invalida.';
    return erros;
}

function quandoPodeTentar(retryAfter: string | null | undefined): number | null {
    const segundos = Number(retryAfter);
    if (Number.isFinite(segundos) && segundos > 0) return Date.now() + segundos * 1_000;

    const data = retryAfter ? Date.parse(retryAfter) : Number.NaN;
    return Number.isFinite(data) && data > Date.now() ? data : null;
}

function mensagemDeErro(erro: unknown, bloqueadoAte: number | null): string {
    const { status, retryAfter } = erro as ErroHttp;
    if (status === 401) return 'E-mail ou senha invalidos.';
    if (status === 422) return 'Revise os dados informados.';
    if (status === 429) {
        const segundos = bloqueadoAte ? Math.ceil((bloqueadoAte - Date.now()) / 1_000) : Number.NaN;
        return Number.isFinite(segundos) && segundos > 0
            ? `Aguarde ${segundos} segundos antes de tentar novamente.`
            : 'Aguarde antes de tentar novamente.';
    }
    if (status === 503 || (typeof status === 'number' && status >= 500)) {
        return 'Servico indisponivel. Tente novamente.';
    }
    return 'Nao foi possivel entrar. Verifique sua conexao e tente novamente.';
}

export function LoginForm({ aoAutenticar, indisponivel = false, aoTentarNovamente }: LoginFormProps) {
    const campoEmail = useRef<HTMLInputElement>(null);
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [erros, setErros] = useState<ErrosCampo>({});
    const [mensagem, setMensagem] = useState<string | null>(null);
    const [enviando, setEnviando] = useState(false);
    const [bloqueadoAte, setBloqueadoAte] = useState<number | null>(null);

    useEffect(() => {
        if (!bloqueadoAte) return undefined;
        const restante = bloqueadoAte - Date.now();
        if (restante <= 0) {
            setBloqueadoAte(null);
            return undefined;
        }
        const temporizador = window.setTimeout(() => setBloqueadoAte(null), restante);
        return () => window.clearTimeout(temporizador);
    }, [bloqueadoAte]);

    async function enviar(evento: FormEvent<HTMLFormElement>) {
        evento.preventDefault();
        if (enviando || (bloqueadoAte && bloqueadoAte > Date.now())) return;

        const proximosErros = validar(email, senha);
        setErros(proximosErros);
        setMensagem(null);
        if (Object.keys(proximosErros).length > 0) {
            campoEmail.current?.focus();
            return;
        }

        setEnviando(true);
        try {
            await aoAutenticar({ email: email.trim().toLowerCase(), senha });
        } catch (erro) {
            const retryAfter = (erro as ErroHttp).retryAfter;
            const proximoEnvio = (erro as ErroHttp).status === 429 ? quandoPodeTentar(retryAfter) : null;
            setBloqueadoAte(proximoEnvio);
            setMensagem(mensagemDeErro(erro, proximoEnvio));
        } finally {
            setSenha('');
            setEnviando(false);
        }
    }

    return (
        <main className="pagina-login">
            {/* A mesma marca do menu lateral do painel (`docs/img_refac/img.png`). */}
            <header className="marca-login">
                <p className="marca-login-nome"><span className="marca-barras" aria-hidden="true">////</span><span>FIAT ARCADE</span></p>
                <h1 id="titulo-login" className="marca-login-subtitulo">Painel administrativo</h1>
            </header>
            <section className="cartao-login" aria-labelledby="titulo-login">
                <h2 className="titulo-cartao-login">Entrar</h2>
                <p className="descricao-login">Entre com suas credenciais de operação.</p>

                {indisponivel && (
                    <div className="aviso-login" role="status">
                        Nao foi possivel verificar a sessao atual.
                        {aoTentarNovamente && (
                            <button type="button" className="botao-texto" onClick={aoTentarNovamente}>
                                Tentar novamente
                            </button>
                        )}
                    </div>
                )}

                <form noValidate onSubmit={enviar}>
                    <div className="campo-login">
                        <label htmlFor="email">E-mail</label>
                        <input
                            id="email"
                            ref={campoEmail}
                            name="email"
                            type="email"
                            autoComplete="username"
                            value={email}
                            onChange={(evento) => setEmail(evento.target.value)}
                            aria-invalid={Boolean(erros.email)}
                            aria-describedby={erros.email ? 'erro-email' : undefined}
                            autoFocus
                        />
                        {erros.email && <p id="erro-email" className="erro-campo">{erros.email}</p>}
                    </div>

                    <div className="campo-login">
                        <label htmlFor="senha">Senha</label>
                        <input
                            id="senha"
                            name="senha"
                            type="password"
                            autoComplete="current-password"
                            value={senha}
                            onChange={(evento) => setSenha(evento.target.value)}
                            aria-invalid={Boolean(erros.senha)}
                            aria-describedby={erros.senha ? 'erro-senha' : undefined}
                        />
                        {erros.senha && <p id="erro-senha" className="erro-campo">{erros.senha}</p>}
                    </div>

                    {mensagem && <p className="mensagem-login" role="alert">{mensagem}</p>}

                    <button className="botao-entrar" type="submit" disabled={enviando || Boolean(bloqueadoAte && bloqueadoAte > Date.now())}>
                        {enviando ? 'Entrando...' : 'Entrar'}
                    </button>
                </form>
            </section>
        </main>
    );
}
